import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import type { PharmacyUser } from '../types';

/**
 * Which pharmacy a pharmacy account works on when it is linked to more than one.
 *
 * - The choice is remembered per user in localStorage, so it survives a reload and the next sign-in.
 * - While the user has more than one pharmacy, the chosen id is kept in sessionStorage under
 *   {@link ACTIVE_PHARMACY_STORAGE_KEY}; the API clients send it as `X-Active-Pharmacy-Id`, and the server applies it to
 *   stock, sales, purchases, register and reports (refusing, 403, a pharmacy the user isn't linked to).
 * - With one pharmacy nothing is sent and the server uses that one, as before.
 */
export const ACTIVE_PHARMACY_STORAGE_KEY = 'active_pharmacy_id';
const CHANGED_EVENT = 'clinvea:active-pharmacy-changed';
const prefKey = (userId: string) => `clinvea.activePharmacy.${userId}`;

function readPreference(userId: string | undefined): string | null {
  if (!userId) return null;
  try { return localStorage.getItem(prefKey(userId)); } catch { return null; }
}

function writePreference(userId: string | undefined, pharmacyId: string) {
  if (!userId) return;
  try { localStorage.setItem(prefKey(userId), pharmacyId); } catch { /* private mode: not remembered */ }
}

function setHeaderValue(pharmacyId: string | null) {
  try {
    if (pharmacyId) sessionStorage.setItem(ACTIVE_PHARMACY_STORAGE_KEY, pharmacyId);
    else sessionStorage.removeItem(ACTIVE_PHARMACY_STORAGE_KEY);
  } catch { /* storage blocked: the server falls back to the first pharmacy */ }
}

/** The active links, and the one to work on: the remembered choice when it is still one of them, else the first. */
export function pickPharmacy(pharmacies: PharmacyUser[] | undefined, preferredId: string | null) {
  const active = (pharmacies ?? []).filter((p) => p.isActive !== false);
  const pharmacy = active.find((p) => p.pharmacyId === preferredId) ?? active[0] ?? null;
  return { active, pharmacy };
}

export function useActivePharmacy(enabled = true) {
  const { userData } = useAuth();
  const userId = userData?.id;
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['myPharmacies'],
    queryFn: () => api.pharmacy.getMyPharmacies(),
    enabled,
    staleTime: 5 * 60_000,
  });
  const [preferredId, setPreferredId] = useState<string | null>(() => readPreference(userId));

  // Another screen (or tab section) switched: follow it.
  useEffect(() => {
    setPreferredId(readPreference(userId));
    const onChange = () => setPreferredId(readPreference(userId));
    window.addEventListener(CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CHANGED_EVENT, onChange);
  }, [userId]);

  const { active, pharmacy } = useMemo(() => pickPharmacy(query.data, preferredId), [query.data, preferredId]);
  const pharmacyId = pharmacy?.pharmacyId ?? null;
  const several = active.length > 1;

  useEffect(() => {
    if (!query.data) return;
    const next = several ? pharmacyId : null;
    let previous: string | null = null;
    try { previous = sessionStorage.getItem(ACTIVE_PHARMACY_STORAGE_KEY); } catch { /* blocked */ }
    if (previous === next) return;
    setHeaderValue(next);
    // A fresh tab: what loaded before the list arrived was answered for the default pharmacy. Reload it.
    if (next) queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'myPharmacies' });
  }, [query.data, several, pharmacyId, queryClient]);

  const setPharmacyId = useCallback((id: string) => {
    if (id === pharmacyId) return;
    writePreference(userId, id);
    setHeaderValue(id);
    setPreferredId(id);
    window.dispatchEvent(new Event(CHANGED_EVENT));
    // Everything on screen belonged to the previous pharmacy.
    queryClient.invalidateQueries();
  }, [pharmacyId, userId, queryClient]);

  return {
    pharmacies: active,
    pharmacy,
    pharmacyId,
    hasSeveral: several,
    setPharmacyId,
    isPending: query.isPending,
    error: query.error,
  };
}
